import { Routes } from "@angular/router";
import { AuthGuard } from "@auth0/auth0-angular";
import { MainLayoutComponent } from "./layouts/main-layout/main-layout.component";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { roleGuard } from "./core/guards/role.guard";
import { kycGuard } from "./core/guards/kyc.guard";
import { firstTimeGuard } from "./core/guards/first-time.guard";
import { Role } from "./core/dtos/user.dto";
import { profileGuard } from "./core/guards/profile.guard";
import { rootGuard } from "./core/guards/root.guard";
import { businessIdGuard } from "./core/guards/business-id.guard";
import { desktopOnlyGuard } from "./core/guards/desktop-only.guard";

export const routes: Routes = [
  {
    path: "signup",
    loadComponent: () => import("./pages/signup/signup.component").then((m) => m.SignupComponent),
  },
  {
    path: "callback",
    loadComponent: () => import("./pages/callback/callback.component").then((m) => m.CallbackComponent),
  },
  {
    path: "onboarding",
    loadComponent: () => import("./pages/onboarding/onboarding.component").then((m) => m.OnboardingComponent),
    canActivate: [AuthGuard, firstTimeGuard],
  },
  {
    path: "onboarding/case-trust",
    loadComponent: () => import("./pages/sign-up/case-sign-up.component").then((m) => m.CaseSignUpComponent),
    canActivate: [AuthGuard, firstTimeGuard],
  },
  {
    path: "signup/case-trust",
    loadComponent: () => import("./pages/sign-up/case-sign-up.component").then((m) => m.CaseSignUpComponent),
  },
  {
    path: "login/case-trust",
    loadComponent: () => import("./pages/sign-up/case-sign-up.component").then((m) => m.CaseSignUpComponent),
  },
  // Legacy case-flow URLs — redirect to case-trust
  { path: "onboarding/case-flow", redirectTo: "onboarding/case-trust", pathMatch: "full" },
  { path: "signup/case-flow", redirectTo: "signup/case-trust", pathMatch: "full" },
  { path: "login/case-flow", redirectTo: "login/case-trust", pathMatch: "full" },
  {
    path: "verification-sent",
    loadComponent: () => import("./pages/verification-sent/verification-sent.component").then((m) => m.VerificationSentComponent),
    canActivate: [AuthGuard, firstTimeGuard],
  },
  {
    path: "verify-email",
    loadComponent: () => import("./pages/verify-email/verify-email.component").then((m) => m.VerifyEmailComponent),
    canActivate: [AuthGuard, firstTimeGuard],
  },
  {
    path: "welcome",
    loadComponent: () => import("./pages/welcome/welcome.component").then((m) => m.WelcomeComponent),
    canActivate: [AuthGuard, firstTimeGuard],
  },
  {
    path: "select-business",
    loadComponent: () => import("./pages/business/business.component").then((m) => m.BusinessComponent),
    canActivate: [AuthGuard, profileGuard],
  },
  {
    path: "organization/:organizationId",
    component: MainLayoutComponent,
    children: [
      {
        path: "dashboard",
        loadComponent: () => import("./pages/organization/organization-dashboard/organization-dashboard.component").then((m) => m.OrganizationDashboardComponent),
      },
    ],
    canActivate: [AuthGuard],
  },
  {
    path: ":businessId",
    component: MainLayoutComponent,
    canActivate: [AuthGuard, profileGuard, businessIdGuard],
    canActivateChild: [kycGuard],
    children: [
      {
        path: "dashboard",
        loadComponent: () => import("./pages/dashboard/dashboard.component").then((m) => m.DashboardComponent),
      },
      {
        path: "dashboard/by-manager/:managerId",
        loadComponent: () => import("./pages/dashboard/pages/by-manager-person-view/by-manager-person-view.component").then((m) => m.ByManagerPersonViewComponent),
        canActivate: [roleGuard],
        data: {
          role: Role.ACCOUNT_MANAGER,
        },
      },
      {
        path: "verify-business",
        loadComponent: () => import("./pages/verify-business/verify-business.component").then((m) => m.VerifyBusinessComponent),
      },
      {
        path: "updates",
        loadComponent: () => import("./pages/updates/updates.component").then((m) => m.UpdatesComponent),
      },
      {
        path: "updates/:conversationId",
        loadComponent: () => import("./pages/updates/pages/chat-detail/chat-detail.component").then((m) => m.ChatDetailComponent),
      },
      {
        path: "projects",
        children: [
          {
            path: "new",
            loadComponent: () => import("./pages/projects/case/new-project-shell/new-project-shell.component").then((m) => m.NewProjectShellComponent),
            canActivate: [desktopOnlyGuard],
          },
          {
            path: "desktop-only",
            loadComponent: () => import("./pages/projects/pages/desktop-only/desktop-only.component").then((m) => m.DesktopOnlyComponent),
          },
          {
            path: "new/type",
            loadComponent: () =>
              import("./pages/projects/case/project-type-chooser/project-type-chooser.component").then((m) => m.ProjectTypeChooserComponent),
            canActivate: [desktopOnlyGuard],
          },
          {
            path: ":projectId",
            loadComponent: () => import("./pages/projects/layouts/project-details-layout/project-details-layout.component").then((m) => m.ProjectDetailsLayoutComponent),
            children: [
              {
                path: "draft",
                loadComponent: () => import("./pages/projects/pages/project-details-draft/project-details-draft.component").then((m) => m.ProjectDetailsDraftComponent),
              },
              {
                path: "in_progress",
                loadComponent: () => import("./pages/projects/pages/project-details-inprogress/project-details-inprogress.component").then((m) => m.ProjectDetailsInprogressComponent),
              },
              {
                path: "disputing",
                loadComponent: () => import("./pages/projects/pages/project-details-inprogress/project-details-inprogress.component").then((m) => m.ProjectDetailsInprogressComponent),
              },
              {
                path: "completed",
                loadComponent: () => import("./pages/projects/pages/project-details-inprogress/project-details-inprogress.component").then((m) => m.ProjectDetailsInprogressComponent),
              },
              {
                path: "",
                loadComponent: () => import("./pages/projects/pages/project-details/project-details.component").then((m) => m.ProjectDetailsComponent),
              },
            ],
          },
          {
            path: "",
            loadComponent: () => import("./pages/projects/projects.component").then((m) => m.ProjectsComponent),
          },
        ],
      },
      {
        path: "clients",
        children: [
          {
            path: ":clientId",
            loadComponent: () => import("./pages/clients/pages/client-details/client-details.component").then((m) => m.ClientDetailsComponent),
          },
          {
            path: "",
            loadComponent: () => import("./pages/clients/clients.component").then((m) => m.ClientsComponent),
          },
        ],
      },
      {
        path: "wallets",
        loadComponent: () => import("./pages/wallets/wallets.component").then((m) => m.WalletsComponent),
        canActivate: [roleGuard],
        data: {
          role: Role.ACCOUNT_MANAGER,
        },
      },
      {
        path: "team",
        children: [
          {
            path: ":userId",
            loadComponent: () => import("./pages/team/pages/team-details/team-details.component").then((m) => m.TeamDetailsComponent),
          },
          {
            path: "",
            loadComponent: () => import("./pages/team/team.component").then((m) => m.TeamComponent),
          },
        ],
        canActivate: [roleGuard],
        data: {
          role: Role.ACCOUNT_MANAGER,
        },
      },
      {
        path: "invoices",
        loadComponent: () => import("./pages/invoices/invoices.component").then((m) => m.InvoicesComponent),
        canActivate: [roleGuard],
        data: {
          // PMs get a self-scoped view (see InvoicesComponent) via the dashboard's "To be collected" card.
          role: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER],
        },
      },
      {
        path: "escrows",
        loadComponent: () => import("./pages/escrows/escrows.component").then((m) => m.EscrowsComponent),
        canActivate: [roleGuard],
        data: {
          // PMs get a self-scoped view (see EscrowsComponent) via the dashboard's "Held in escrow" card.
          role: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER],
        },
      },
      {
        path: "profile",
        loadComponent: () => import("./pages/profile/profile.component").then((m) => m.ProfileComponent),
      },
      {
        path: "settings/accounting",
        loadComponent: () => import("./pages/accounting-settings/accounting-settings.component").then((m) => m.AccountingSettingsComponent),
        canActivate: [roleGuard],
        data: {
          role: Role.ACCOUNT_MANAGER,
        },
      },
      {
        path: "",
        redirectTo: "projects",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "",
    canActivate: [rootGuard],
    children: [],
  },
  { path: "**", component: NotFoundComponent },
];
